import type { ProposalPayload } from '../api/proposals'
import { colors } from '../theme'

interface Props {
  value: ProposalPayload
  onChange: (next: ProposalPayload) => void
  confidence?: Record<string, 'high' | 'medium' | 'low'>
  readOnly?: boolean
}

// Field caps mirror app/services/proposal/models.py.
const CAPS: Record<keyof Pick<ProposalPayload, 'prospect' | 'company' | 'city' | 'rep' | 'email' | 'date'>, number> = {
  prospect: 20,
  company: 35,
  city: 25,
  rep: 30,
  email: 35,
  date: 15,
}

export default function ProposalFields({ value, onChange, confidence, readOnly }: Props) {
  function setField<K extends keyof ProposalPayload>(key: K, next: ProposalPayload[K]) {
    onChange({ ...value, [key]: next })
  }

  const sublineLen = `${value.prospect}  ·  ${value.company}, ${value.city}`.length
  const sublineOver = sublineLen > 65

  return (
    <div
      style={{
        background: colors.white,
        border: `1px solid ${colors.divider}`,
        borderRadius: 12,
        padding: 24,
        display: 'grid',
        gap: 18,
        boxShadow: '0 2px 8px rgba(35,31,32,0.04)',
      }}
    >
      <SectionTitle>Cover identity</SectionTitle>

      <Grid>
        <Input
          label="Prospect"
          value={value.prospect}
          onChange={(v) => setField('prospect', v)}
          max={CAPS.prospect}
          conf={confidence?.prospect}
          readOnly={readOnly}
        />
        <Input
          label="Company / property"
          value={value.company}
          onChange={(v) => setField('company', v)}
          max={CAPS.company}
          conf={confidence?.company}
          readOnly={readOnly}
        />
      </Grid>

      <Grid>
        <Input
          label="City"
          value={value.city}
          onChange={(v) => setField('city', v)}
          max={CAPS.city}
          conf={confidence?.city}
          readOnly={readOnly}
        />
        <Input
          label="Date"
          value={value.date}
          onChange={(v) => setField('date', v)}
          max={CAPS.date}
          conf={confidence?.date}
          readOnly={readOnly}
        />
      </Grid>

      <Grid>
        <Input
          label="Rep (you)"
          value={value.rep}
          onChange={(v) => setField('rep', v)}
          max={CAPS.rep}
          conf={confidence?.rep}
          readOnly={readOnly}
        />
        <Input
          label="Rep email"
          value={value.email}
          onChange={(v) => setField('email', v)}
          max={CAPS.email}
          conf={confidence?.email}
          readOnly={readOnly}
        />
      </Grid>

      <div
        style={{
          fontSize: 11,
          color: sublineOver ? colors.red : colors.textMuted,
          fontFamily: 'ui-monospace, monospace',
          paddingTop: 8,
          borderTop: `1px solid ${colors.divider}`,
          fontWeight: sublineOver ? 700 : 400,
        }}
      >
        Cover sub-line: {sublineLen}/65 chars{' '}
        {sublineOver ? '· trim prospect, company, or city' : '· OK'}
      </div>

      <div style={{ borderTop: `1px solid ${colors.divider}`, paddingTop: 18 }}>
        <SectionTitle>Pricing emphasis · optional</SectionTitle>
        <div style={{ display: 'grid', gap: 14, marginTop: 14 }}>
          <Select
            label="Recommended plan"
            value={value.pricing?.recommended_plan ?? 'portfolio'}
            options={[
              { value: 'single', label: 'Single Property (1–2 zones)' },
              { value: 'portfolio', label: 'Portfolio (3+ zones, recommended)' },
            ]}
            onChange={(v) =>
              onChange({
                ...value,
                pricing: { ...(value.pricing ?? {}), recommended_plan: v as 'single' | 'portfolio' },
              })
            }
            readOnly={readOnly}
          />
          <Input
            label="Pricing callout — headline"
            placeholder="e.g. Surface lot only — we'll size to 40 spots"
            value={value.pricing?.callout?.headline ?? ''}
            onChange={(v) =>
              onChange({
                ...value,
                pricing: {
                  ...(value.pricing ?? {}),
                  callout: { headline: v, detail: value.pricing?.callout?.detail ?? '' },
                },
              })
            }
            readOnly={readOnly}
          />
          <Input
            label="Pricing callout — detail"
            placeholder="e.g. Replace Flowbird meters; QR-only deploy, LPR add-on later."
            value={value.pricing?.callout?.detail ?? ''}
            onChange={(v) =>
              onChange({
                ...value,
                pricing: {
                  ...(value.pricing ?? {}),
                  callout: { headline: value.pricing?.callout?.headline ?? '', detail: v },
                },
              })
            }
            readOnly={readOnly}
          />
        </div>
      </div>
    </div>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '2.5px',
        textTransform: 'uppercase',
        color: colors.purple,
      }}
    >
      {children}
    </div>
  )
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
      {children}
    </div>
  )
}

function Input({
  label,
  value,
  onChange,
  max,
  conf,
  readOnly,
  placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  max?: number
  conf?: 'high' | 'medium' | 'low'
  readOnly?: boolean
  placeholder?: string
}) {
  const over = typeof max === 'number' && value.length > max
  return (
    <label style={{ display: 'block' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          fontSize: 10,
          marginBottom: 6,
          color: colors.black,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          fontWeight: 700,
        }}
      >
        <span>
          {label}
          {conf ? <ConfidenceTag conf={conf} /> : null}
        </span>
        {typeof max === 'number' ? (
          <span
            style={{
              color: over ? colors.red : colors.textDim,
              fontWeight: over ? 700 : 400,
              letterSpacing: 0,
              textTransform: 'none',
            }}
          >
            {value.length}/{max}
          </span>
        ) : null}
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '10px 12px',
          background: readOnly ? colors.bgAlt : colors.white,
          color: colors.black,
          border: `1px solid ${over ? colors.red : colors.divider}`,
          borderRadius: 8,
          fontSize: 14,
          outline: 'none',
        }}
      />
    </label>
  )
}

function Select({
  label,
  value,
  options,
  onChange,
  readOnly,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
  readOnly?: boolean
}) {
  return (
    <label style={{ display: 'block' }}>
      <div
        style={{
          fontSize: 10,
          marginBottom: 6,
          color: colors.black,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={readOnly}
        style={{
          width: '100%',
          padding: '10px 12px',
          background: readOnly ? colors.bgAlt : colors.white,
          color: colors.black,
          border: `1px solid ${colors.divider}`,
          borderRadius: 8,
          fontSize: 14,
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function ConfidenceTag({ conf }: { conf: 'high' | 'medium' | 'low' }) {
  if (conf === 'high') return null
  // Use Stack Red for "guessed" and Stack Orange for "check"; both are
  // accent-only per brand, applied as soft tinted chips.
  const bg = conf === 'low' ? 'rgba(237,28,36,0.12)' : 'rgba(241,101,33,0.14)'
  const fg = conf === 'low' ? colors.red : colors.orange
  const label = conf === 'low' ? 'Guessed' : 'Check'
  return (
    <span
      style={{
        marginLeft: 8,
        padding: '2px 7px',
        borderRadius: 4,
        background: bg,
        color: fg,
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '1px',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </span>
  )
}
